import React from "react";
import { Eye } from "lucide-react";
import { useAuth } from "@/lib/auth";

/**
 * DemoBanner — sticky "DEMO VIEW" ribbon shown at the top of admin
 * screens whenever a `is_demo` buyer-preview account is signed in.
 *
 * Why: buyers can see every admin panel but every number has been zeroed
 * on the backend and every write returns 403. Without this banner they'd
 * wonder why actions appear broken; with it, the demo framing is obvious
 * at a glance and reinforces the pre-launch "fresh slate" positioning.
 *
 * Renders nothing for non-demo users, so it is safe to drop onto any
 * admin page unconditionally.
 */
export default function DemoBanner() {
  const { user } = useAuth();
  if (!user?.is_demo) return null;
  return (
    <div
      data-testid="demo-banner"
      className="mb-6 rounded-lg px-4 py-3 flex items-start gap-3"
      style={{
        background:
          "linear-gradient(90deg, rgba(0,240,255,0.08) 0%, rgba(204,255,0,0.08) 100%)",
        border: "1px solid rgba(0,240,255,0.35)",
      }}
    >
      <Eye
        className="w-4 h-4 mt-0.5 flex-shrink-0"
        style={{ color: "var(--sc-cyan)" }}
      />
      <div className="flex-1 leading-snug">
        <div
          className="sc-mono text-[11px] font-bold tracking-widest mb-0.5"
          style={{ color: "var(--sc-cyan)" }}
        >
          DEMO VIEW · READ-ONLY WALKTHROUGH
        </div>
        <div className="text-xs text-zinc-300">
          You&apos;re previewing MystiCast as a potential buyer. All metrics
          reflect a brand-new pre-launch site (zero activity), and every
          action is disabled. Session expires in 2&nbsp;hours.
        </div>
      </div>
    </div>
  );
}
